/**
 * MobileOrderCard — the Trioty mobile order card (smart view). Header: avatar +
 * #code / customer name on the left, total + relative date on the right. Below:
 * the reusable status badge, a payment pill, and the item count. An optional
 * action slot (quick-action button) sits under a hairline for actionable rows.
 */

import type { ReactNode } from "react";
import type { SellerOrderListItem } from "@/lib/api/types";
import { useLocale, useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { getMoneyState } from "@/modules/orders/lib/order-money";
import { relativeAgo } from "@/modules/orders/lib/order-status-display";
import { resolveItemTitle } from "@/modules/orders/lib/resolve-item-title";
import { getOrderItemImageUrl } from "@/lib/order-image";
import { ImageThumb } from "@/shared/components/PlaceholderImage";
import { OrderStatusBadge } from "../order-row/order-visuals";

export function MobileOrderCard({
  order,
  onSelect,
  action,
  muted = false,
  compact = false,
}: {
  order: SellerOrderListItem;
  onSelect: () => void;
  action?: ReactNode;
  muted?: boolean;
  /** Dense single-row layout for the mobile "list" view toggle. */
  compact?: boolean;
}) {
  const locale = useLocale();
  const t = useTranslations();
  const money = getMoneyState(
    order.status,
    order.paymentMethod,
    order.paidAmount,
    order.requiredAmount,
    order.totalAmount,
  );
  const date = relativeAgo(order.createdAt, locale);
  const firstItem = order.items[0] ?? null;

  if (compact) {
    return (
      <div
        role="button"
        tabIndex={0}
        onClick={onSelect}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") onSelect();
        }}
        className={[
          "flex cursor-pointer items-center justify-between gap-2.5 rounded-[12px] border border-[color:var(--card-br)] px-3 py-2.5 transition-colors",
          muted ? "bg-[color:var(--soft2)] opacity-95" : "bg-[color:var(--card)]",
        ].join(" ")}
      >
        <div className="flex min-w-0 items-center gap-2.5">
          <ImageThumb
            src={firstItem ? getOrderItemImageUrl(firstItem) : null}
            seed={firstItem?.productId}
            alt={firstItem ? resolveItemTitle(firstItem, locale) : ""}
            size="h-9 w-9"
            bg="bg-white"
            className="rounded-[9px]"
          />
          <div className="min-w-0">
            <div dir="ltr" className="truncate font-mono text-[12.5px] font-bold tracking-[0.01em] text-[color:var(--text-primary)]">
              #{order.orderCode}
            </div>
            <div className="mt-0.5 flex items-center gap-1.5">
              <OrderStatusBadge status={order.status} />
            </div>
          </div>
        </div>
        <div className="shrink-0 text-end">
          <div className="text-[13.5px] font-bold text-[color:var(--text-primary)]">
            {money.headlineFormatted}
          </div>
          <div className="mt-px text-[10.5px] text-[color:var(--text-muted)]">
            {t(keys.orders.itemsCountSummary, { count: order.itemCount })}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") onSelect();
      }}
      className={[
        "flex cursor-pointer flex-col gap-3 rounded-[16px] border border-[color:var(--card-br)] p-[14px] transition-colors",
        muted ? "bg-[color:var(--soft2)] opacity-95" : "bg-[color:var(--card)]",
      ].join(" ")}
    >
      {/* Header: item image + #code/name · total/date */}
      <div className="flex items-center justify-between gap-2.5">
        <div className="flex min-w-0 items-center gap-[11px]">
          <ImageThumb
            src={firstItem ? getOrderItemImageUrl(firstItem) : null}
            seed={firstItem?.productId}
            alt={firstItem ? resolveItemTitle(firstItem, locale) : ""}
            size="h-10 w-10"
            bg="bg-white"
            className="rounded-[10px]"
          />
          <div className="min-w-0">
            <div dir="ltr" className="truncate font-mono text-[13.5px] font-bold tracking-[0.01em] text-[color:var(--text-primary)]">
              #{order.orderCode}
            </div>
            <div className="truncate text-[11.5px] text-[color:var(--text-muted)]">
              {order.customer.name || "—"}
            </div>
          </div>
        </div>
        <div className="shrink-0 text-end">
          <div className="text-[15px] font-bold text-[color:var(--text-primary)]">
            {money.headlineFormatted}
          </div>
          {date ? (
            <div className="mt-px text-[11px] text-[color:var(--text-muted)]">{date}</div>
          ) : null}
        </div>
      </div>

      {/* Status badge · payment · items */}
      <div className="flex flex-wrap items-center gap-2">
        <OrderStatusBadge status={order.status} />
        <span
          className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-[3px] text-[11px] font-semibold leading-none"
          style={{
            background: `color-mix(in srgb, ${money.chipColor} 14%, var(--card))`,
            color: money.chipColor,
          }}
        >
          <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full" style={{ background: money.chipColor }} />
          {t(money.chipKey)}
        </span>
        <span className="ms-auto shrink-0 text-[11.5px] text-[color:var(--text-muted)]">
          {t(keys.orders.itemsCountSummary, { count: order.itemCount })}
        </span>
      </div>

      {action ? (
        <div
          className="border-t border-[color:var(--hair)] pt-3"
          onClick={(e) => e.stopPropagation()}
        >
          {action}
        </div>
      ) : null}
    </div>
  );
}
